import { isNil } from 'es-toolkit';
import { DateTime } from 'luxon';

import type { HistoricalValue } from '@savvy/constants/generated/gql-types';
import type { FragmentOf } from '@savvy/deprecated-shared/gql-compat';
import type { ChartDataPoint } from '@savvy/deprecated-shared/x-components/x-line-chart';
import { PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES } from '@savvy/constants/portfolio-item-groups';
import { PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES } from '@savvy/deprecated-shared/constants';
import { graphql as tadaGraphql, unmaskFragment } from '@savvy/deprecated-shared/gql-compat';
import { getFirstDateOfData } from '@savvy/deprecated-shared/utils/net-worth';
import { hasKey } from '@savvy/utils/typescript';

export function fromDateStringToISO(date: string): number {
  return Math.floor(new Date(date).getTime());
}

export function generateEmptyDateObjects(lastIsoDate: string, lookbackPeriodDays: number) {
  const lastDate = DateTime.fromISO(lastIsoDate);
  const result: Record<string, Record<string, number>> = {};
  for (let i = lookbackPeriodDays - 1; i >= 0; i--) {
    const historicalDate = lastDate.minus({ days: i }).toISODate();
    result[historicalDate] = {};
  }

  return result;
}

// Spread by this package's x-pages (client-preset). apps/web can no longer spread it:
// its codegen glob is rooted at apps/web/src and client-preset rejects a fragment
// it cannot find in its own document set — hence PortfolioItemGroupGraphData below.
export const GenerateDataForPortfolioItemGroupsFragment = tadaGraphql(/* GraphQL */ `
  fragment GenerateDataForPortfolioItemGroups on PortfolioItemGroup {
    name
    accounts {
      __typename
    }
    assets {
      __typename
    }
    hiddenFromClientHistoricalValues {
      date
      value {
        amount
        currencyCode
      }
    }
    historicalValues {
      date
      value {
        amount
        currencyCode
      }
    }
    liabilities {
      __typename
    }
  }
`);

export type PortfolioItemGroupGraphData = {
  name: string;
  accounts: readonly unknown[];
  assets: readonly unknown[];
  liabilities: readonly unknown[];
  hiddenFromClientHistoricalValues: readonly HistoricalValue[];
  historicalValues: readonly HistoricalValue[];
};

export function generateDataForPortfolioItemGroups(
  portfolioItemGroups: readonly PortfolioItemGroupGraphData[],
  lookbackPeriodDays: number,
  showHiddenClientAssetsInGraph: boolean,
) {
  const dateObjectDictionary = generateDateObjectDictionary(
    portfolioItemGroups,
    lookbackPeriodDays,
    showHiddenClientAssetsInGraph,
  );
  return Object.keys(dateObjectDictionary).map((dateKey) => {
    return {
      name: dateKey,
      ...dateObjectDictionary[dateKey],
    };
  });
}

/**
 * All of the logic in this file is a bit of a mess that's hard to follow
 * given some of the loose typing in the past. This function compromises
 * slightly by having stricter input/output typing while reusing a lot of
 * the untyped helper functions
 * @param portfolioItemGroupFrags
 * @param lookbackPeriodDays
 * @param showHiddenClientAssetsInGraph
 * @returns
 */
export function generateDataForXPortfolioItemGroups(
  portfolioItemGroupFrags: FragmentOf<typeof GenerateDataForPortfolioItemGroupsFragment>[],
  lookbackPeriodDays: number | null,
  showHiddenClientAssetsInGraph: boolean,
): ChartDataPoint[] {
  const dateObjectDictionary = generateDateObjectDictionary(
    portfolioItemGroupFrags.map((frag) => unmaskFragment(GenerateDataForPortfolioItemGroupsFragment, frag)),
    lookbackPeriodDays,
    showHiddenClientAssetsInGraph,
  );
  return Object.keys(dateObjectDictionary).map((dateKey) => {
    return {
      x: fromDateStringToISO(dateKey),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      y1: Object.values(dateObjectDictionary[dateKey]!).reduce((acc, curr) => acc + curr, 0),
    };
  });
}

function mergeHistoricalValues(
  array1: readonly HistoricalValue[],
  array2: readonly HistoricalValue[],
): HistoricalValue[] {
  const mergedMap: Record<string, number> = {};

  // Merge values from array1
  for (const item of array1) {
    const {
      date,
      value: { amount },
    } = item;
    const parsedAmount = parseFloat(amount);
    if (date in mergedMap) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      mergedMap[date]! += parsedAmount;
    } else {
      mergedMap[date] = parsedAmount;
    }
  }

  // Merge values from array2
  for (const item of array2) {
    const {
      date,
      value: { amount },
    } = item;
    const parsedAmount = parseFloat(amount);
    if (date in mergedMap) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
      mergedMap[date]! += parsedAmount;
    } else {
      mergedMap[date] = parsedAmount;
    }
  }

  // Convert mergedMap back to the desired format
  const mergedArray: HistoricalValue[] = [];
  for (const date in mergedMap) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
    const amount = mergedMap[date]!.toFixed(2);
    mergedArray.push({
      value: {
        amount,
        currencyCode: 'USD',
        __typename: 'Value',
      },
      date,
      __typename: 'HistoricalValue',
    });
  }

  return mergedArray;
}

function generateDateObjectDictionary(
  portfolioItemGroups: readonly PortfolioItemGroupGraphData[],
  lookbackPeriodDays: number | null,
  showHiddenClientAssetsInGraph: boolean,
) {
  const latestDate = getLatestDate(portfolioItemGroups);
  const earliestDate = getFirstDateOfData(portfolioItemGroups);

  if (!latestDate || !earliestDate) {
    return {};
  }

  const daysWithData = DateTime.fromISO(latestDate).diff(DateTime.fromISO(earliestDate), 'days').days + 1;

  let lookbackPeriodDaysFromDaysWithData = lookbackPeriodDays;
  if (daysWithData < 6) {
    lookbackPeriodDaysFromDaysWithData = daysWithData + 2;
  } else if (daysWithData === 6) {
    lookbackPeriodDaysFromDaysWithData = daysWithData + 1;
  }
  const dateObjectDictionary = generateEmptyDateObjects(latestDate, lookbackPeriodDaysFromDaysWithData ?? daysWithData);

  portfolioItemGroups
    .filter(
      (portfolioItemGroup) =>
        portfolioItemGroup.accounts.length + portfolioItemGroup.assets.length + portfolioItemGroup.liabilities.length >
        0,
    )
    .forEach((portfolioItemGroup) => {
      const historicalValues = showHiddenClientAssetsInGraph
        ? mergeHistoricalValues(
            portfolioItemGroup.historicalValues,
            portfolioItemGroup.hiddenFromClientHistoricalValues,
          )
        : portfolioItemGroup.historicalValues;
      const historicalValueDictionary = generateHistoricalValueDictionary(historicalValues);
      const dateKeys = Object.keys(dateObjectDictionary);
      const displayName = hasKey(PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES, portfolioItemGroup.name)
        ? PORTFOLIO_ITEM_GROUP_DISPLAY_NAMES[portfolioItemGroup.name]
        : portfolioItemGroup.name;
      for (let i = 0; i < dateKeys.length; i++) {
        const dateKey = dateKeys[i];
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
        const historicalValue = historicalValueDictionary[dateKey!];
        let amount;
        if (!isNil(historicalValue)) {
          amount = getSignedAmount(historicalValue.value.amount, portfolioItemGroup.name);
        } else if (i > 0) {
          const previousDateKey = dateKeys[i - 1];
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
          amount = dateObjectDictionary[previousDateKey!]![displayName];
        } else {
          amount = 0;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
        dateObjectDictionary[dateKey!]![displayName] = amount!;
      }
    });
  return dateObjectDictionary;
}

function getLatestDate(portfolioItemGroups: readonly PortfolioItemGroupGraphData[]): string | null {
  const groupWithHistory = portfolioItemGroups.find((group) => {
    return group.historicalValues.length > 0;
  });
  if (!groupWithHistory) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: This rule was recently enabled for the repo
  return groupWithHistory.historicalValues[groupWithHistory.historicalValues.length - 1]!.date;
}

function generateHistoricalValueDictionary(
  historicalValues: readonly HistoricalValue[],
): Record<string, HistoricalValue | undefined> {
  return Object.fromEntries(
    new Map(
      historicalValues.map((historicalValue) => {
        return [historicalValue.date, historicalValue];
      }),
    ),
  );
}

function getSignedAmount(amount: string, portfolioItemGroupName: string): number {
  if (portfolioItemGroupName === PORTFOLIO_ITEM_GROUP_OTHER_LIABILITIES) {
    return -parseFloat(amount);
  }
  return parseFloat(amount);
}
